
/**
 * @file services/calorieApi.js
 * @description Calorie & nutrition estimation service.
 *
 * Strategy (in order):
 *   1. Tries the FREE Nutritionix Natural Language API (no key needed for basic use).
 *   2. Falls back to a local keyword-based estimation table so the app always
 *      returns something useful even without a network call.
 *
 * To upgrade to AI-powered estimation, swap `estimateWithAI()` in and point it at
 * any LLM API (OpenAI, Anthropic, etc.) with the prompt template already written below.
 * Just add your API key to a .env file as REACT_APP_OPENAI_KEY or similar.
 */

// ── Local fallback DB ─────────────────────────────────────────────────────
/** @type {Array<{ keywords: string[], cal: number, pro: number, per: string }>} */

const LOCAL_DB = [
  // --- Proteins (Gym & Quality Meats) ---
  {
    keywords: ["chicken breast", "sadour", "chicken fillet"],
    cal: 165,
    pro: 31,
    per: "100g",
  },
  {
    keywords: ["chicken thigh", "werrak", "shish"],
    cal: 209,
    pro: 26,
    per: "100g",
  },
  {
    keywords: ["egg", "eggs", "beid", "bayd"],
    cal: 70,
    pro: 6,
    per: "1 large",
  },
  { keywords: ["egg white", "beid bayad"], cal: 17, pro: 4, per: "1 large" },
  { keywords: ["beef", "lahma", "steak"], cal: 250, pro: 26, per: "100g" },
  {
    keywords: ["minced meat", "lahma mafrouma"],
    cal: 240,
    pro: 24,
    per: "100g",
  },
  { keywords: ["tuna", "tuna can"], cal: 115, pro: 26, per: "1 can 120g" },
  { keywords: ["salmon"], cal: 200, pro: 20, per: "100g" },
  {
    keywords: ["white fish", "bolti", "fillet"],
    cal: 100,
    pro: 18,
    per: "100g",
  },
  { keywords: ["shrimp", "gambari"], cal: 99, pro: 24, per: "100g" },
  {
    keywords: ["turkey", "roumi", "cold cuts"],
    cal: 105,
    pro: 22,
    per: "100g",
  },
  {
    keywords: ["protein shake", "whey", "scoop", "isowhey"],
    cal: 120,
    pro: 25,
    per: "1 scoop",
  },
  { keywords: ["liver", "kebda"], cal: 175, pro: 27, per: "100g" },

  // --- Carbs, Grains & Breads ---
  {
    keywords: ["white rice", "roz abiad"],
    cal: 130,
    pro: 2.5,
    per: "100g cooked",
  },
  {
    keywords: ["basmati rice", "roz basmati"],
    cal: 120,
    pro: 3,
    per: "100g cooked",
  },
  {
    keywords: ["oats", "oatmeal", "shofaan"],
    cal: 370,
    pro: 13,
    per: "100g dry",
  },
  { keywords: ["pasta", "makarona"], cal: 130, pro: 5, per: "100g cooked" },
  { keywords: ["potato", "batates"], cal: 80, pro: 2, per: "100g" },
  { keywords: ["sweet potato", "batata"], cal: 90, pro: 2, per: "100g" },
  { keywords: ["bread", "eish", "toast"], cal: 80, pro: 3, per: "1 slice" },
  {
    keywords: ["pita", "baladi", "eish baladi"],
    cal: 160,
    pro: 6,
    per: "1 pita",
  },
  { keywords: ["tortilla", "wrap"], cal: 150, pro: 4, per: "1 medium" },
  { keywords: ["quinoa"], cal: 120, pro: 4, per: "100g cooked" },
  { keywords: ["cornflakes", "cereal"], cal: 360, pro: 7, per: "100g" },

  // --- Egyptian Staples & Cooked Meals ---
  { keywords: ["foul", "ful", "medames"], cal: 110, pro: 8, per: "100g" },
  { keywords: ["falafel", "taameya"], cal: 60, pro: 2, per: "1 piece" },
  { keywords: ["lentils", "ads"], cal: 116, pro: 9, per: "100g cooked" },
  { keywords: ["koshary", "koshari"], cal: 180, pro: 6, per: "100g" },
  { keywords: ["molokhia", "mulukhiyah"], cal: 45, pro: 3, per: "100g" },
  {
    keywords: ["mahshi", "stuffed pepper", "kromb"],
    cal: 150,
    pro: 3,
    per: "100g",
  },
  { keywords: ["hawawshi"], cal: 350, pro: 18, per: "1/2 loaf" },
  { keywords: ["besara"], cal: 105, pro: 7, per: "100g" },
  { keywords: ["mousakaa"], cal: 140, pro: 4, per: "100g" },

  // --- Dairy & Healthy Fats ---
  { keywords: ["milk", "laban"], cal: 50, pro: 3.5, per: "100ml" },
  { keywords: ["greek yogurt", "zabadi"], cal: 60, pro: 10, per: "100g" },
  {
    keywords: ["cottage cheese", "gibna areesh"],
    cal: 98,
    pro: 11,
    per: "100g",
  },
  { keywords: ["cheese", "gibna", "feta"], cal: 265, pro: 14, per: "100g" },
  { keywords: ["cheddar", "roumi cheese"], cal: 400, pro: 25, per: "100g" },
  { keywords: ["olive oil", "zet"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["peanut butter"], cal: 190, pro: 8, per: "2 tbsp" },
  { keywords: ["butter", "zebda"], cal: 100, pro: 0, per: "1 tbsp" },
  { keywords: ["avocado"], cal: 160, pro: 2, per: "100g" },

  // --- Fruits ---
  { keywords: ["banana", "mouz"], cal: 90, pro: 1, per: "1 medium" },
  { keywords: ["apple", "tofah"], cal: 80, pro: 0.5, per: "1 medium" },
  { keywords: ["orange", "bortoan"], cal: 60, pro: 1, per: "1 medium" },
  { keywords: ["dates", "balah", "tamr"], cal: 20, pro: 0.2, per: "1 date" },
  { keywords: ["mango", "manga"], cal: 60, pro: 0.8, per: "100g" },
  { keywords: ["watermelon", "batikh"], cal: 30, pro: 0.6, per: "100g" },
  { keywords: ["grapes", "enab"], cal: 67, pro: 0.7, per: "100g" },
  { keywords: ["strawberries", "farawla"], cal: 33, pro: 0.7, per: "100g" },

  // --- Vegetables ---
  { keywords: ["cucumber", "khyar"], cal: 15, pro: 0.5, per: "100g" },
  { keywords: ["tomato", "tamatem"], cal: 18, pro: 0.9, per: "100g" },
  { keywords: ["broccoli"], cal: 34, pro: 2.8, per: "100g" },
  { keywords: ["spinach", "sabanekh"], cal: 23, pro: 2.9, per: "100g" },
  { keywords: ["carrot", "gazar"], cal: 41, pro: 0.9, per: "100g" },
  { keywords: ["green beans", "fasolya"], cal: 31, pro: 1.8, per: "100g" },

  // --- Snacks, Nuts & Treats ---
  { keywords: ["almonds", "lawz"], cal: 7, pro: 0.3, per: "1 nut" },
  { keywords: ["walnuts", "ein gamal"], cal: 26, pro: 0.6, per: "1 nut" },
  { keywords: ["pistachios", "fostok"], cal: 4, pro: 0.1, per: "1 nut" },
  { keywords: ["cashews", "kaju"], cal: 9, pro: 0.3, per: "1 nut" },
  { keywords: ["honey", "asal"], cal: 60, pro: 0, per: "1 tbsp" },
  { keywords: ["dark chocolate"], cal: 155, pro: 2, per: "30g" },
  { keywords: ["sugar", "sokkar"], cal: 16, pro: 0, per: "1 tsp" },

  // --- Fast Food & Restaurant ---
  { keywords: ["shawarma", "shwarma"], cal: 450, pro: 28, per: "1 wrap" },
  { keywords: ["burger"], cal: 250, pro: 15, per: "1 small patty" },
  { keywords: ["pizza"], cal: 285, pro: 12, per: "1 slice" },
  { keywords: ["fries", "french fries"], cal: 312, pro: 3, per: "100g" },
  { keywords: ["nuggets"], cal: 45, pro: 2, per: "1 piece" },
  // ═══════════════════════════════════════════════════════════════════
  // PROTEINS — Poultry
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["chicken breast", "sadour", "chicken fillet"], cal: 165, pro: 31, per: "100g" },
  { keywords: ["chicken thigh", "werrak", "shish"], cal: 209, pro: 26, per: "100g" },
  { keywords: ["chicken wing", "ganaH"], cal: 203, pro: 24, per: "100g" },
  { keywords: ["chicken leg", "regl farakha"], cal: 184, pro: 25, per: "100g" },
  { keywords: ["chicken drumstick", "sa'a"], cal: 172, pro: 23, per: "100g" },
  { keywords: ["chicken kebab", "kebab farakha"], cal: 185, pro: 22, per: "100g" },
  { keywords: ["grilled chicken", "farakha mashwiya"], cal: 165, pro: 30, per: "100g" },
  { keywords: ["fried chicken", "farakha maqleya"], cal: 320, pro: 22, per: "100g" },
  { keywords: ["chicken shawarma meat"], cal: 195, pro: 24, per: "100g" },
  { keywords: ["roast chicken", "farakha rosto"], cal: 200, pro: 27, per: "100g" },
  { keywords: ["duck", "batta"], cal: 337, pro: 19, per: "100g" },
  { keywords: ["turkey breast", "roumi"], cal: 135, pro: 30, per: "100g" },
  { keywords: ["turkey mince", "lahma roumi"], cal: 170, pro: 21, per: "100g" },
  { keywords: ["quail", "summan"], cal: 234, pro: 25, per: "100g" },
  { keywords: ["pigeon", "hamam"], cal: 294, pro: 19, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // PROTEINS — Red Meat
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["beef", "lahma", "steak"], cal: 250, pro: 26, per: "100g" },
  { keywords: ["minced meat", "lahma mafrouma"], cal: 240, pro: 24, per: "100g" },
  { keywords: ["veal", "batello"], cal: 175, pro: 20, per: "100g" },
  { keywords: ["lamb", "lahma daani"], cal: 295, pro: 25, per: "100g" },
  { keywords: ["lamb chop", "riyash"], cal: 310, pro: 24, per: "100g" },
  { keywords: ["kofta", "kufta"], cal: 230, pro: 18, per: "100g" },
  { keywords: ["kebab", "grilled kofta"], cal: 220, pro: 18, per: "100g" },
  { keywords: ["liver", "kebda"], cal: 175, pro: 27, per: "100g" },
  { keywords: ["heart", "alb"], cal: 165, pro: 28, per: "100g" },
  { keywords: ["kidney", "kelawi"], cal: 157, pro: 26, per: "100g" },
  { keywords: ["veal liver", "kebda batello"], cal: 192, pro: 29, per: "100g" },
  { keywords: ["sausage", "sogguk", "naqaniq"], cal: 300, pro: 14, per: "100g" },
  { keywords: ["hot dog", "frankfurter"], cal: 280, pro: 12, per: "100g" },
  { keywords: ["pepperoni"], cal: 494, pro: 22, per: "100g" },
  { keywords: ["salami"], cal: 380, pro: 19, per: "100g" },
  { keywords: ["corned beef", "lahma el'ilba"], cal: 250, pro: 29, per: "100g" },
  { keywords: ["pastrami"], cal: 139, pro: 19, per: "100g" },
  { keywords: ["pork", "khanzir"], cal: 297, pro: 25, per: "100g" },
  { keywords: ["bacon"], cal: 541, pro: 37, per: "100g" },
  { keywords: ["beef jerky", "lahma mawdana"], cal: 250, pro: 33, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // PROTEINS — Seafood
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["tuna", "tuna can"], cal: 115, pro: 26, per: "1 can 120g" },
  { keywords: ["tuna steak", "tuna fresh"], cal: 144, pro: 30, per: "100g" },
  { keywords: ["salmon"], cal: 200, pro: 20, per: "100g" },
  { keywords: ["salmon fillet", "salmon maqli"], cal: 220, pro: 22, per: "100g" },
  { keywords: ["white fish", "bolti", "fillet"], cal: 100, pro: 18, per: "100g" },
  { keywords: ["sea bass", "qarous"], cal: 124, pro: 24, per: "100g" },
  { keywords: ["mullet", "bouri"], cal: 117, pro: 20, per: "100g" },
  { keywords: ["red snapper", "zoreidah"], cal: 100, pro: 21, per: "100g" },
  { keywords: ["shrimp", "gambari"], cal: 99, pro: 24, per: "100g" },
  { keywords: ["calamari", "squid", "sobar"], cal: 92, pro: 16, per: "100g" },
  { keywords: ["fried calamari", "sobar maqli"], cal: 175, pro: 13, per: "100g" },
  { keywords: ["crab", "saratan"], cal: 87, pro: 18, per: "100g" },
  { keywords: ["lobster", "estakoza"], cal: 89, pro: 19, per: "100g" },
  { keywords: ["oyster", "mahara"], cal: 68, pro: 7, per: "100g" },
  { keywords: ["sardine", "sardeen"], cal: 208, pro: 25, per: "100g" },
  { keywords: ["mackerel", "eskabre"], cal: 205, pro: 19, per: "100g" },
  { keywords: ["herring"], cal: 158, pro: 18, per: "100g" },
  { keywords: ["tilapia", "bolti mashwi"], cal: 96, pro: 20, per: "100g" },
  { keywords: ["catfish", "qarmout"], cal: 105, pro: 18, per: "100g" },
  { keywords: ["smoked salmon", "salmon mudakhan"], cal: 117, pro: 18, per: "100g" },
  { keywords: ["anchovy", "anshuga"], cal: 210, pro: 29, per: "100g" },
  { keywords: ["fish fingers", "esba' samak"], cal: 236, pro: 14, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // PROTEINS — Eggs & Dairy Proteins
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["egg", "eggs", "beid", "bayd"], cal: 70, pro: 6, per: "1 large" },
  { keywords: ["egg white", "beid bayad"], cal: 17, pro: 4, per: "1 large" },
  { keywords: ["egg yolk", "safar el beid"], cal: 55, pro: 2.7, per: "1 large" },
  { keywords: ["boiled egg", "beid maslou"], cal: 68, pro: 6, per: "1 large" },
  { keywords: ["fried egg", "beid maqli"], cal: 90, pro: 6, per: "1 large" },
  { keywords: ["scrambled eggs", "beid mkhaffa"], cal: 91, pro: 6, per: "1 large" },
  { keywords: ["omelette", "imlit"], cal: 154, pro: 11, per: "2 eggs" },

  // ═══════════════════════════════════════════════════════════════════
  // PROTEINS — Plant-Based & Legumes
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["foul", "ful", "medames"], cal: 110, pro: 8, per: "100g" },
  { keywords: ["lentils", "ads"], cal: 116, pro: 9, per: "100g cooked" },
  { keywords: ["red lentils", "ads ahmar"], cal: 116, pro: 9, per: "100g cooked" },
  { keywords: ["chickpeas", "hommos"], cal: 164, pro: 9, per: "100g cooked" },
  { keywords: ["hummus", "hommos bil tahina"], cal: 177, pro: 8, per: "100g" },
  { keywords: ["black beans"], cal: 132, pro: 9, per: "100g cooked" },
  { keywords: ["kidney beans", "fasolya hamra"], cal: 127, pro: 9, per: "100g cooked" },
  { keywords: ["white beans", "fasolya bida"], cal: 139, pro: 9, per: "100g cooked" },
  { keywords: ["edamame"], cal: 121, pro: 11, per: "100g" },
  { keywords: ["soy", "soya", "tofu"], cal: 76, pro: 8, per: "100g" },
  { keywords: ["tempeh"], cal: 193, pro: 19, per: "100g" },
  { keywords: ["seitan"], cal: 120, pro: 25, per: "100g" },
  { keywords: ["falafel", "taameya"], cal: 60, pro: 2, per: "1 piece" },
  { keywords: ["peas", "bisilla"], cal: 81, pro: 5, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // GYM & SUPPLEMENTS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["protein shake", "whey", "scoop", "isowhey"], cal: 120, pro: 25, per: "1 scoop" },
  { keywords: ["whey isolate"], cal: 110, pro: 27, per: "1 scoop" },
  { keywords: ["casein protein", "night protein"], cal: 120, pro: 24, per: "1 scoop" },
  { keywords: ["plant protein", "vegan protein"], cal: 125, pro: 22, per: "1 scoop" },
  { keywords: ["mass gainer", "weight gainer"], cal: 400, pro: 30, per: "1 serving" },
  { keywords: ["creatine"], cal: 0, pro: 0, per: "5g" },
  { keywords: ["bcaa"], cal: 10, pro: 5, per: "1 serving" },
  { keywords: ["protein bar"], cal: 200, pro: 20, per: "1 bar" },
  { keywords: ["quest bar"], cal: 190, pro: 21, per: "1 bar" },
  { keywords: ["granola bar", "bar habboub"], cal: 190, pro: 4, per: "1 bar" },
  { keywords: ["energy bar"], cal: 230, pro: 5, per: "1 bar" },
  { keywords: ["pre workout"], cal: 10, pro: 0, per: "1 scoop" },

  // ═══════════════════════════════════════════════════════════════════
  // CARBS — Grains, Rice & Pasta
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["white rice", "roz abiad"], cal: 130, pro: 2.5, per: "100g cooked" },
  { keywords: ["basmati rice", "roz basmati"], cal: 120, pro: 3, per: "100g cooked" },
  { keywords: ["brown rice", "roz asmar"], cal: 112, pro: 2.6, per: "100g cooked" },
  { keywords: ["jasmine rice"], cal: 129, pro: 2.4, per: "100g cooked" },
  { keywords: ["fried rice"], cal: 163, pro: 3, per: "100g" },
  { keywords: ["oats", "oatmeal", "shofaan"], cal: 370, pro: 13, per: "100g dry" },
  { keywords: ["instant oats", "oats fasoria"], cal: 360, pro: 12, per: "100g" },
  { keywords: ["pasta", "makarona"], cal: 130, pro: 5, per: "100g cooked" },
  { keywords: ["spaghetti"], cal: 130, pro: 5, per: "100g cooked" },
  { keywords: ["penne", "penne makarona"], cal: 131, pro: 5, per: "100g cooked" },
  { keywords: ["fusilli"], cal: 130, pro: 5, per: "100g cooked" },
  { keywords: ["lasagna", "lazania"], cal: 135, pro: 5, per: "100g cooked" },
  { keywords: ["noodles", "noodlz"], cal: 138, pro: 4.5, per: "100g cooked" },
  { keywords: ["rice noodles"], cal: 109, pro: 2, per: "100g cooked" },
  { keywords: ["couscous", "koskos"], cal: 112, pro: 4, per: "100g cooked" },
  { keywords: ["bulgur", "burghol"], cal: 83, pro: 3, per: "100g cooked" },
  { keywords: ["quinoa"], cal: 120, pro: 4, per: "100g cooked" },
  { keywords: ["cornflakes", "cereal"], cal: 360, pro: 7, per: "100g" },
  { keywords: ["granola"], cal: 450, pro: 10, per: "100g" },
  { keywords: ["muesli"], cal: 340, pro: 11, per: "100g" },
  { keywords: ["wheat germ"], cal: 360, pro: 23, per: "100g" },
  { keywords: ["bran", "nakhala"], cal: 216, pro: 15, per: "100g" },
  { keywords: ["barley", "sha'ir"], cal: 123, pro: 2.3, per: "100g cooked" },
  { keywords: ["freekeh", "firik"], cal: 330, pro: 13, per: "100g dry" },

  // ═══════════════════════════════════════════════════════════════════
  // CARBS — Bread & Baked
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["bread", "eish", "toast"], cal: 80, pro: 3, per: "1 slice" },
  { keywords: ["white bread", "eish abiad"], cal: 80, pro: 2.7, per: "1 slice" },
  { keywords: ["brown bread", "eish asmar"], cal: 70, pro: 3.5, per: "1 slice" },
  { keywords: ["whole wheat bread", "eish kamil"], cal: 69, pro: 3.6, per: "1 slice" },
  { keywords: ["sourdough", "eish homadi"], cal: 90, pro: 3.5, per: "1 slice" },
  { keywords: ["pita", "baladi", "eish baladi"], cal: 160, pro: 6, per: "1 pita" },
  { keywords: ["pita chips", "eish baladi maqli"], cal: 130, pro: 3, per: "30g" },
  { keywords: ["tortilla", "wrap"], cal: 150, pro: 4, per: "1 medium" },
  { keywords: ["roti", "chapati"], cal: 130, pro: 4, per: "1 piece" },
  { keywords: ["naan"], cal: 260, pro: 9, per: "1 piece" },
  { keywords: ["bagel"], cal: 250, pro: 10, per: "1 medium" },
  { keywords: ["croissant", "krwasan"], cal: 270, pro: 5, per: "1 medium" },
  { keywords: ["english muffin"], cal: 130, pro: 4, per: "1 piece" },
  { keywords: ["baguette", "french bread"], cal: 270, pro: 9, per: "100g" },
  { keywords: ["crackers", "baskweet"], cal: 430, pro: 9, per: "100g" },
  { keywords: ["rice cake", "kaik roz"], cal: 35, pro: 0.7, per: "1 cake" },
  { keywords: ["corn tortilla"], cal: 52, pro: 1.4, per: "1 piece" },
  { keywords: ["pancake", "pankek"], cal: 90, pro: 2.5, per: "1 medium" },
  { keywords: ["waffle"], cal: 130, pro: 4, per: "1 waffle" },

  // ═══════════════════════════════════════════════════════════════════
  // CARBS — Starchy Vegetables
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["potato", "batates"], cal: 80, pro: 2, per: "100g" },
  { keywords: ["sweet potato", "batata"], cal: 90, pro: 2, per: "100g" },
  { keywords: ["mashed potato", "batates mahrosa"], cal: 115, pro: 2, per: "100g" },
  { keywords: ["baked potato", "batates fil forn"], cal: 93, pro: 2.5, per: "100g" },
  { keywords: ["fries", "french fries"], cal: 312, pro: 3, per: "100g" },
  { keywords: ["sweet potato fries", "batata maqliya"], cal: 151, pro: 1.5, per: "100g" },
  { keywords: ["corn", "dora"], cal: 96, pro: 3.4, per: "100g cooked" },
  { keywords: ["corn on the cob", "dora mashwiya"], cal: 86, pro: 3.3, per: "1 ear" },
  { keywords: ["popcorn", "feshar"], cal: 375, pro: 11, per: "100g" },
  { keywords: ["cassava", "tapioca"], cal: 160, pro: 1.4, per: "100g" },
  { keywords: ["plantain", "mouz kebir"], cal: 122, pro: 1.3, per: "100g" },
  { keywords: ["yam"], cal: 118, pro: 1.5, per: "100g" },
  { keywords: ["taro", "qolqas"], cal: 112, pro: 1.5, per: "100g" },
  { keywords: ["pumpkin", "qara"], cal: 26, pro: 1, per: "100g" },
  { keywords: ["butternut squash", "qara 3asali"], cal: 45, pro: 1, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // EGYPTIAN STAPLES & COOKED MEALS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["koshary", "koshari"], cal: 180, pro: 6, per: "100g" },
  { keywords: ["molokhia", "mulukhiyah"], cal: 45, pro: 3, per: "100g" },
  { keywords: ["mahshi", "stuffed pepper", "kromb"], cal: 150, pro: 3, per: "100g" },
  { keywords: ["hawawshi"], cal: 350, pro: 18, per: "1/2 loaf" },
  { keywords: ["besara"], cal: 105, pro: 7, per: "100g" },
  { keywords: ["mousakaa", "moussaka"], cal: 140, pro: 4, per: "100g" },
  { keywords: ["fatteh", "fatta"], cal: 195, pro: 7, per: "100g" },
  { keywords: ["roz bil laban", "rice pudding"], cal: 125, pro: 4, per: "100g" },
  { keywords: ["om ali"], cal: 310, pro: 6, per: "100g" },
  { keywords: ["feteer", "fetir"], cal: 280, pro: 7, per: "100g" },
  { keywords: ["feteer mishaltit", "flaky pie"], cal: 320, pro: 7, per: "100g" },
  { keywords: ["fitfit"], cal: 280, pro: 6, per: "100g" },
  { keywords: ["konafa", "knafeh"], cal: 320, pro: 5, per: "100g" },
  { keywords: ["basbousa", "semolina cake"], cal: 320, pro: 5, per: "100g" },
  { keywords: ["kahk", "eid cookies"], cal: 460, pro: 6, per: "100g" },
  { keywords: ["qatayef", "atayef"], cal: 270, pro: 6, per: "1 piece" },
  { keywords: ["sambousek", "sambousa"], cal: 280, pro: 8, per: "100g" },
  { keywords: ["baba ghanoush", "mutabbal"], cal: 73, pro: 2, per: "100g" },
  { keywords: ["taboula", "tabbouleh"], cal: 70, pro: 2, per: "100g" },
  { keywords: ["fattoush"], cal: 90, pro: 2, per: "100g" },
  { keywords: ["stuffed vine leaves", "wara' einab"], cal: 90, pro: 3, per: "1 piece" },
  { keywords: ["vine leaves", "wara3 3enab"], cal: 90, pro: 3, per: "1 piece" },
  { keywords: ["kebda eskandarani", "spicy liver"], cal: 200, pro: 24, per: "100g" },
  { keywords: ["fattah", "fatta bil roz"], cal: 200, pro: 8, per: "100g" },
  { keywords: ["ta'leya", "taleya"], cal: 90, pro: 1, per: "1 tbsp" },
  { keywords: ["shorba", "soup"], cal: 40, pro: 2, per: "100ml" },
  { keywords: ["adass shorba", "lentil soup"], cal: 95, pro: 6, per: "100ml" },
  { keywords: ["tomato soup", "shorbet tamatem"], cal: 42, pro: 1, per: "100ml" },
  { keywords: ["chicken soup", "shorbet farakha"], cal: 49, pro: 4, per: "100ml" },
  { keywords: ["harira", "harira soup"], cal: 88, pro: 4, per: "100ml" },
  { keywords: ["bamia", "okra"], cal: 33, pro: 2, per: "100g" },
  { keywords: ["bamia bil lahma", "okra with meat"], cal: 90, pro: 7, per: "100g" },
  { keywords: ["kofta with tomato sauce", "kofta bil salsa"], cal: 210, pro: 16, per: "100g" },
  { keywords: ["daoud basha"], cal: 215, pro: 14, per: "100g" },
  { keywords: ["lamb tagine", "tagine daani"], cal: 195, pro: 16, per: "100g" },
  { keywords: ["hawawshi small"], cal: 175, pro: 9, per: "1 piece" },
  { keywords: ["macarona béchamel", "macarona bil beshamel"], cal: 165, pro: 7, per: "100g" },
  { keywords: ["sayadeya", "fish with rice"], cal: 175, pro: 10, per: "100g" },
  { keywords: ["mulukhiya with chicken"], cal: 80, pro: 7, per: "100g" },
  { keywords: ["roz muammar"], cal: 180, pro: 4, per: "100g" },
  { keywords: ["balila", "boiled chickpeas"], cal: 180, pro: 9, per: "100g" },
  { keywords: ["shaak", "bran soup"], cal: 55, pro: 3, per: "100ml" },

  // ═══════════════════════════════════════════════════════════════════
  // INTERNATIONAL CUISINE
  // ═══════════════════════════════════════════════════════════════════
  // Italian
  { keywords: ["carbonara", "pasta carbonara"], cal: 220, pro: 10, per: "100g" },
  { keywords: ["bolognese", "pasta bolognese"], cal: 185, pro: 12, per: "100g" },
  { keywords: ["alfredo", "pasta alfredo"], cal: 210, pro: 8, per: "100g" },
  { keywords: ["risotto"], cal: 164, pro: 4, per: "100g" },
  { keywords: ["gnocchi"], cal: 130, pro: 3, per: "100g" },
  { keywords: ["ravioli"], cal: 143, pro: 7, per: "100g" },
  { keywords: ["tiramisu"], cal: 350, pro: 6, per: "100g" },
  { keywords: ["bruschetta"], cal: 176, pro: 4, per: "100g" },
  { keywords: ["calzone"], cal: 265, pro: 13, per: "100g" },

  // Mexican
  { keywords: ["taco", "tacos"], cal: 226, pro: 9, per: "1 taco" },
  { keywords: ["burrito"], cal: 310, pro: 15, per: "1 burrito" },
  { keywords: ["quesadilla"], cal: 200, pro: 10, per: "100g" },
  { keywords: ["nachos"], cal: 480, pro: 10, per: "100g" },
  { keywords: ["guacamole", "avocado dip"], cal: 150, pro: 2, per: "100g" },
  { keywords: ["salsa"], cal: 36, pro: 1.6, per: "100g" },
  { keywords: ["enchilada"], cal: 170, pro: 10, per: "100g" },
  { keywords: ["fajita"], cal: 160, pro: 12, per: "100g" },

  // Indian
  { keywords: ["biryani"], cal: 200, pro: 8, per: "100g" },
  { keywords: ["chicken biryani"], cal: 210, pro: 12, per: "100g" },
  { keywords: ["dal", "dahl"], cal: 130, pro: 9, per: "100g" },
  { keywords: ["butter chicken", "murgh makhani"], cal: 150, pro: 12, per: "100g" },
  { keywords: ["tikka masala", "chicken tikka"], cal: 145, pro: 13, per: "100g" },
  { keywords: ["samosa"], cal: 262, pro: 6, per: "1 piece" },
  { keywords: ["palak paneer"], cal: 165, pro: 8, per: "100g" },
  { keywords: ["paneer"], cal: 265, pro: 18, per: "100g" },
  { keywords: ["chana masala"], cal: 160, pro: 8, per: "100g" },
  { keywords: ["chapati", "roti"], cal: 130, pro: 4, per: "1 piece" },

  // Japanese
  { keywords: ["sushi roll"], cal: 50, pro: 2.5, per: "1 piece" },
  { keywords: ["sashimi", "raw fish"], cal: 130, pro: 22, per: "100g" },
  { keywords: ["ramen"], cal: 190, pro: 9, per: "100g" },
  { keywords: ["miso soup"], cal: 40, pro: 3, per: "1 cup" },
  { keywords: ["edamame"], cal: 121, pro: 11, per: "100g" },
  { keywords: ["teriyaki chicken", "teriyaki"], cal: 190, pro: 20, per: "100g" },
  { keywords: ["gyoza", "dumplings"], cal: 70, pro: 4, per: "1 piece" },
  { keywords: ["onigiri", "rice ball"], cal: 190, pro: 4, per: "1 piece" },
  { keywords: ["udon"], cal: 105, pro: 3, per: "100g cooked" },
  { keywords: ["soba noodles"], cal: 99, pro: 5, per: "100g cooked" },
  { keywords: ["tempura"], cal: 300, pro: 10, per: "100g" },
  { keywords: ["katsu curry"], cal: 220, pro: 14, per: "100g" },

  // Chinese
  { keywords: ["fried rice", "roz maqli"], cal: 163, pro: 3, per: "100g" },
  { keywords: ["chow mein"], cal: 145, pro: 6, per: "100g" },
  { keywords: ["dim sum"], cal: 55, pro: 3, per: "1 piece" },
  { keywords: ["kung pao chicken"], cal: 180, pro: 17, per: "100g" },
  { keywords: ["orange chicken"], cal: 200, pro: 12, per: "100g" },
  { keywords: ["spring roll"], cal: 110, pro: 3, per: "1 roll" },
  { keywords: ["wonton soup"], cal: 55, pro: 3, per: "1 wonton" },
  { keywords: ["peking duck"], cal: 339, pro: 19, per: "100g" },
  { keywords: ["mapo tofu"], cal: 100, pro: 7, per: "100g" },

  // Turkish & Levantine
  { keywords: ["shawarma", "shwarma"], cal: 450, pro: 28, per: "1 wrap" },
  { keywords: ["shawarma meat only"], cal: 195, pro: 24, per: "100g" },
  { keywords: ["mujadara"], cal: 130, pro: 5, per: "100g" },
  { keywords: ["manakish", "manoushe"], cal: 350, pro: 9, per: "1 piece" },
  { keywords: ["falafel wrap"], cal: 330, pro: 10, per: "1 wrap" },
  { keywords: ["labneh"], cal: 178, pro: 9, per: "100g" },
  { keywords: ["kibbeh", "kubba"], cal: 185, pro: 12, per: "100g" },
  { keywords: ["kofta kebab levantine"], cal: 215, pro: 17, per: "100g" },
  { keywords: ["shish tawook"], cal: 175, pro: 22, per: "100g" },
  { keywords: ["fatayer"], cal: 250, pro: 7, per: "1 piece" },
  { keywords: ["lubia", "green bean stew"], cal: 70, pro: 3, per: "100g" },

  // Fast Food
  { keywords: ["burger"], cal: 250, pro: 15, per: "1 small patty" },
  { keywords: ["cheeseburger"], cal: 350, pro: 18, per: "1 burger" },
  { keywords: ["big mac", "big mak"], cal: 550, pro: 27, per: "1 burger" },
  { keywords: ["whopper"], cal: 660, pro: 28, per: "1 burger" },
  { keywords: ["chicken sandwich", "chicken burger"], cal: 400, pro: 25, per: "1 burger" },
  { keywords: ["double cheeseburger"], cal: 460, pro: 25, per: "1 burger" },
  { keywords: ["pizza"], cal: 285, pro: 12, per: "1 slice" },
  { keywords: ["margherita pizza"], cal: 250, pro: 10, per: "1 slice" },
  { keywords: ["pepperoni pizza"], cal: 310, pro: 14, per: "1 slice" },
  { keywords: ["chicken pizza"], cal: 270, pro: 14, per: "1 slice" },
  { keywords: ["nuggets"], cal: 45, pro: 2, per: "1 piece" },
  { keywords: ["chicken nuggets 6pc"], cal: 270, pro: 14, per: "6 pieces" },
  { keywords: ["hot dog bun"], cal: 280, pro: 11, per: "1 hot dog" },
  { keywords: ["fish sandwich", "fish fillet sandwich"], cal: 380, pro: 19, per: "1 sandwich" },
  { keywords: ["mozzarella sticks"], cal: 280, pro: 12, per: "4 sticks" },
  { keywords: ["onion rings"], cal: 276, pro: 4, per: "100g" },
  { keywords: ["mac and cheese", "macaroni cheese"], cal: 164, pro: 6, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // DAIRY & ALTERNATIVES
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["milk", "laban"], cal: 50, pro: 3.5, per: "100ml" },
  { keywords: ["full fat milk", "laban kamil"], cal: 61, pro: 3.2, per: "100ml" },
  { keywords: ["skim milk", "laban khafiif"], cal: 34, pro: 3.4, per: "100ml" },
  { keywords: ["semi skimmed milk"], cal: 47, pro: 3.3, per: "100ml" },
  { keywords: ["almond milk"], cal: 17, pro: 0.4, per: "100ml" },
  { keywords: ["oat milk"], cal: 45, pro: 1, per: "100ml" },
  { keywords: ["soy milk", "laban soya"], cal: 42, pro: 3.3, per: "100ml" },
  { keywords: ["coconut milk"], cal: 230, pro: 2.3, per: "100ml" },
  { keywords: ["greek yogurt", "zabadi"], cal: 60, pro: 10, per: "100g" },
  { keywords: ["regular yogurt", "zabadi 3adi"], cal: 63, pro: 5, per: "100g" },
  { keywords: ["low fat yogurt", "zabadi light"], cal: 47, pro: 4, per: "100g" },
  { keywords: ["kefir"], cal: 61, pro: 3.3, per: "100ml" },
  { keywords: ["cottage cheese", "gibna areesh"], cal: 98, pro: 11, per: "100g" },
  { keywords: ["ricotta"], cal: 174, pro: 11, per: "100g" },
  { keywords: ["cream cheese", "jibna krima"], cal: 350, pro: 6, per: "100g" },
  { keywords: ["mozzarella", "mozzarella jibna"], cal: 280, pro: 22, per: "100g" },
  { keywords: ["parmesan"], cal: 431, pro: 38, per: "100g" },
  { keywords: ["cheddar", "roumi cheese"], cal: 400, pro: 25, per: "100g" },
  { keywords: ["cheese", "gibna", "feta"], cal: 265, pro: 14, per: "100g" },
  { keywords: ["feta cheese", "jibna feta"], cal: 265, pro: 14, per: "100g" },
  { keywords: ["brie"], cal: 334, pro: 21, per: "100g" },
  { keywords: ["gouda"], cal: 356, pro: 25, per: "100g" },
  { keywords: ["edam"], cal: 357, pro: 25, per: "100g" },
  { keywords: ["processed cheese", "jibna moshakala"], cal: 300, pro: 18, per: "100g" },
  { keywords: ["cream", "qeshta", "heavy cream"], cal: 340, pro: 2, per: "100ml" },
  { keywords: ["whipped cream"], cal: 257, pro: 2, per: "100ml" },
  { keywords: ["sour cream"], cal: 198, pro: 2.4, per: "100ml" },
  { keywords: ["butter", "zebda"], cal: 100, pro: 0, per: "1 tbsp" },
  { keywords: ["ghee", "samna"], cal: 112, pro: 0, per: "1 tbsp" },
  { keywords: ["ice cream", "ays krim"], cal: 207, pro: 3.5, per: "100g" },
  { keywords: ["gelato"], cal: 160, pro: 3, per: "100g" },
  { keywords: ["frozen yogurt", "froyo"], cal: 130, pro: 3, per: "100g" },
  { keywords: ["pudding"], cal: 130, pro: 3, per: "100g" },
  { keywords: ["custard", "crème"], cal: 122, pro: 4, per: "100g" },
  { keywords: ["evaporated milk", "laban mukaththaf"], cal: 134, pro: 7, per: "100ml" },
  { keywords: ["condensed milk", "laban halwa"], cal: 321, pro: 8, per: "100ml" },
  { keywords: ["labneh", "strained yogurt"], cal: 178, pro: 9, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // FATS & OILS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["olive oil", "zet"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["sunflower oil", "zet 3abbad"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["corn oil", "zet dora"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["coconut oil", "zet goz el hind"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["vegetable oil", "zet nabati"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["sesame oil", "zet simsim"], cal: 90, pro: 0, per: "1 tbsp" },
  { keywords: ["peanut butter"], cal: 190, pro: 8, per: "2 tbsp" },
  { keywords: ["almond butter"], cal: 200, pro: 7, per: "2 tbsp" },
  { keywords: ["tahini", "tahina"], cal: 170, pro: 5, per: "2 tbsp" },
  { keywords: ["avocado"], cal: 160, pro: 2, per: "100g" },
  { keywords: ["mayonnaise", "mayyonaise"], cal: 94, pro: 0.1, per: "1 tbsp" },
  { keywords: ["light mayo", "lite mayonnaise"], cal: 49, pro: 0.1, per: "1 tbsp" },
  { keywords: ["margarine", "nilit"], cal: 75, pro: 0.1, per: "1 tbsp" },
  { keywords: ["lard"], cal: 115, pro: 0, per: "1 tbsp" },

  // ═══════════════════════════════════════════════════════════════════
  // NUTS & SEEDS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["almonds", "lawz"], cal: 7, pro: 0.3, per: "1 nut" },
  { keywords: ["almonds 100g"], cal: 579, pro: 21, per: "100g" },
  { keywords: ["walnuts", "ein gamal"], cal: 26, pro: 0.6, per: "1 nut" },
  { keywords: ["walnuts 100g"], cal: 654, pro: 15, per: "100g" },
  { keywords: ["pistachios", "fostok"], cal: 4, pro: 0.1, per: "1 nut" },
  { keywords: ["pistachios 100g"], cal: 562, pro: 20, per: "100g" },
  { keywords: ["cashews", "kaju"], cal: 9, pro: 0.3, per: "1 nut" },
  { keywords: ["cashews 100g"], cal: 553, pro: 18, per: "100g" },
  { keywords: ["peanuts", "ful sudani"], cal: 567, pro: 26, per: "100g" },
  { keywords: ["hazelnuts", "bunduq"], cal: 628, pro: 15, per: "100g" },
  { keywords: ["macadamia nuts"], cal: 718, pro: 8, per: "100g" },
  { keywords: ["pecans", "bikaan"], cal: 691, pro: 9, per: "100g" },
  { keywords: ["brazil nuts"], cal: 656, pro: 14, per: "100g" },
  { keywords: ["sunflower seeds", "bizr"], cal: 584, pro: 21, per: "100g" },
  { keywords: ["pumpkin seeds", "bizr qar'"], cal: 559, pro: 30, per: "100g" },
  { keywords: ["sesame seeds", "simsim"], cal: 573, pro: 18, per: "100g" },
  { keywords: ["flaxseed", "katan"], cal: 534, pro: 18, per: "100g" },
  { keywords: ["chia seeds"], cal: 486, pro: 17, per: "100g" },
  { keywords: ["hemp seeds"], cal: 553, pro: 32, per: "100g" },
  { keywords: ["mixed nuts", "mukassarat"], cal: 600, pro: 16, per: "100g" },
  { keywords: ["trail mix"], cal: 460, pro: 11, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // FRUITS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["banana", "mouz"], cal: 90, pro: 1, per: "1 medium" },
  { keywords: ["apple", "tofah"], cal: 80, pro: 0.5, per: "1 medium" },
  { keywords: ["orange", "bortoan"], cal: 60, pro: 1, per: "1 medium" },
  { keywords: ["dates", "balah", "tamr"], cal: 20, pro: 0.2, per: "1 date" },
  { keywords: ["mango", "manga"], cal: 60, pro: 0.8, per: "100g" },
  { keywords: ["watermelon", "batikh"], cal: 30, pro: 0.6, per: "100g" },
  { keywords: ["grapes", "enab"], cal: 67, pro: 0.7, per: "100g" },
  { keywords: ["strawberries", "farawla"], cal: 33, pro: 0.7, per: "100g" },
  { keywords: ["blueberries", "toot azraq"], cal: 57, pro: 0.7, per: "100g" },
  { keywords: ["raspberries", "toot"], cal: 52, pro: 1.2, per: "100g" },
  { keywords: ["blackberries", "toot akhdar"], cal: 43, pro: 1.4, per: "100g" },
  { keywords: ["cherries", "karaz"], cal: 63, pro: 1.1, per: "100g" },
  { keywords: ["peach", "khokh"], cal: 39, pro: 0.9, per: "100g" },
  { keywords: ["pear", "koumithra"], cal: 57, pro: 0.4, per: "100g" },
  { keywords: ["plum", "barquq"], cal: 46, pro: 0.7, per: "100g" },
  { keywords: ["apricot", "mishmish"], cal: 48, pro: 1.4, per: "100g" },
  { keywords: ["fig", "teen"], cal: 74, pro: 0.8, per: "100g" },
  { keywords: ["pomegranate", "rommaan"], cal: 83, pro: 1.7, per: "100g" },
  { keywords: ["kiwi", "kiwi fruit"], cal: 61, pro: 1.1, per: "100g" },
  { keywords: ["pineapple", "ananas"], cal: 50, pro: 0.5, per: "100g" },
  { keywords: ["papaya", "babaya"], cal: 43, pro: 0.5, per: "100g" },
  { keywords: ["guava", "gawafa"], cal: 68, pro: 2.6, per: "100g" },
  { keywords: ["lemon", "limoun"], cal: 29, pro: 1.1, per: "100g" },
  { keywords: ["lime", "limoun akhdar"], cal: 30, pro: 0.7, per: "100g" },
  { keywords: ["coconut", "goz el hind"], cal: 354, pro: 3.3, per: "100g" },
  { keywords: ["dragon fruit", "fruit taneen"], cal: 50, pro: 1.2, per: "100g" },
  { keywords: ["lychee", "laitshi"], cal: 66, pro: 0.8, per: "100g" },
  { keywords: ["passion fruit", "markisa"], cal: 97, pro: 2.2, per: "100g" },
  { keywords: ["cantaloupe", "shamam"], cal: 34, pro: 0.8, per: "100g" },
  { keywords: ["honeydew", "battikh 3asali"], cal: 36, pro: 0.5, per: "100g" },
  { keywords: ["grape fruit", "grapefruit", "greibfrout"], cal: 42, pro: 0.8, per: "100g" },
  { keywords: ["raisins", "zabib"], cal: 299, pro: 3.1, per: "100g" },
  { keywords: ["prunes", "barquq mujaffaf"], cal: 240, pro: 2.2, per: "100g" },
  { keywords: ["dried mango", "manga mujaffafa"], cal: 319, pro: 1.8, per: "100g" },
  { keywords: ["dried apricot", "mishmish mujaffaf"], cal: 241, pro: 3.4, per: "100g" },
  { keywords: ["cranberries dried"], cal: 308, pro: 0.2, per: "100g" },
  { keywords: ["mulberry", "tout el shajar"], cal: 43, pro: 1.4, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // VEGETABLES — Non-starchy
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["cucumber", "khyar"], cal: 15, pro: 0.5, per: "100g" },
  { keywords: ["tomato", "tamatem"], cal: 18, pro: 0.9, per: "100g" },
  { keywords: ["broccoli"], cal: 34, pro: 2.8, per: "100g" },
  { keywords: ["spinach", "sabanekh"], cal: 23, pro: 2.9, per: "100g" },
  { keywords: ["carrot", "gazar"], cal: 41, pro: 0.9, per: "100g" },
  { keywords: ["green beans", "fasolya"], cal: 31, pro: 1.8, per: "100g" },
  { keywords: ["lettuce", "khass"], cal: 15, pro: 1.4, per: "100g" },
  { keywords: ["romaine lettuce", "khass romani"], cal: 17, pro: 1.2, per: "100g" },
  { keywords: ["iceberg lettuce"], cal: 14, pro: 0.9, per: "100g" },
  { keywords: ["kale"], cal: 49, pro: 4.3, per: "100g" },
  { keywords: ["arugula", "garjeer"], cal: 25, pro: 2.6, per: "100g" },
  { keywords: ["cabbage", "kromb"], cal: 25, pro: 1.3, per: "100g" },
  { keywords: ["red cabbage", "kromb ahmar"], cal: 31, pro: 1.4, per: "100g" },
  { keywords: ["cauliflower", "zahret"], cal: 25, pro: 1.9, per: "100g" },
  { keywords: ["eggplant", "batingan"], cal: 25, pro: 1, per: "100g" },
  { keywords: ["zucchini", "kousa"], cal: 17, pro: 1.2, per: "100g" },
  { keywords: ["bell pepper", "filfil"], cal: 31, pro: 1, per: "100g" },
  { keywords: ["red pepper", "filfil ahmar"], cal: 31, pro: 1, per: "100g" },
  { keywords: ["green pepper", "filfil akhdar"], cal: 20, pro: 0.9, per: "100g" },
  { keywords: ["onion", "bassal"], cal: 40, pro: 1.1, per: "100g" },
  { keywords: ["red onion", "bassal ahmar"], cal: 40, pro: 1.1, per: "100g" },
  { keywords: ["spring onion", "bassal akhdar"], cal: 32, pro: 1.8, per: "100g" },
  { keywords: ["garlic", "toum"], cal: 149, pro: 6.4, per: "100g" },
  { keywords: ["leek", "korra"], cal: 61, pro: 1.5, per: "100g" },
  { keywords: ["celery", "karafs"], cal: 16, pro: 0.7, per: "100g" },
  { keywords: ["asparagus", "hlyoun"], cal: 20, pro: 2.2, per: "100g" },
  { keywords: ["mushroom", "futr"], cal: 22, pro: 3.1, per: "100g" },
  { keywords: ["shiitake mushroom"], cal: 39, pro: 2.2, per: "100g" },
  { keywords: ["artichoke", "khurshuf"], cal: 47, pro: 3.3, per: "100g" },
  { keywords: ["radish", "figl"], cal: 16, pro: 0.7, per: "100g" },
  { keywords: ["beet", "beets", "shamandar"], cal: 43, pro: 1.6, per: "100g" },
  { keywords: ["beetroot", "shamandar"], cal: 43, pro: 1.6, per: "100g" },
  { keywords: ["fennel", "shamaar"], cal: 31, pro: 1.2, per: "100g" },
  { keywords: ["bok choy"], cal: 13, pro: 1.5, per: "100g" },
  { keywords: ["watercress", "rashad"], cal: 11, pro: 2.3, per: "100g" },
  { keywords: ["rocket salad", "jarjeer"], cal: 25, pro: 2.6, per: "100g" },
  { keywords: ["mixed salad", "salata makhlouta"], cal: 20, pro: 1.5, per: "100g" },
  { keywords: ["greek salad"], cal: 90, pro: 3, per: "100g" },
  { keywords: ["caesar salad no croutons"], cal: 70, pro: 4, per: "100g" },
  { keywords: ["coleslaw"], cal: 130, pro: 1.2, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // CONDIMENTS, SAUCES & SPREADS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["ketchup", "ketshap"], cal: 19, pro: 0.2, per: "1 tbsp" },
  { keywords: ["mustard", "mostarda"], cal: 9, pro: 0.5, per: "1 tbsp" },
  { keywords: ["hot sauce", "salsa harra"], cal: 5, pro: 0.2, per: "1 tbsp" },
  { keywords: ["sriracha"], cal: 15, pro: 0.2, per: "1 tbsp" },
  { keywords: ["soy sauce", "salsa soya"], cal: 8, pro: 1.3, per: "1 tbsp" },
  { keywords: ["teriyaki sauce"], cal: 30, pro: 0.6, per: "1 tbsp" },
  { keywords: ["barbecue sauce", "bbq sauce"], cal: 29, pro: 0.3, per: "1 tbsp" },
  { keywords: ["ranch dressing"], cal: 73, pro: 0.3, per: "1 tbsp" },
  { keywords: ["caesar dressing"], cal: 78, pro: 0.7, per: "1 tbsp" },
  { keywords: ["balsamic vinegar", "khal balsamic"], cal: 14, pro: 0.1, per: "1 tbsp" },
  { keywords: ["vinegar", "khal"], cal: 3, pro: 0, per: "1 tbsp" },
  { keywords: ["honey mustard"], cal: 30, pro: 0.2, per: "1 tbsp" },
  { keywords: ["tahini sauce", "salsa tahina"], cal: 90, pro: 3, per: "1 tbsp" },
  { keywords: ["pesto"], cal: 120, pro: 4, per: "1 tbsp" },
  { keywords: ["tomato sauce", "salsa tamatem"], cal: 15, pro: 0.5, per: "1 tbsp" },
  { keywords: ["pasta sauce", "salsa makarona"], cal: 30, pro: 1, per: "1 tbsp" },
  { keywords: ["jam", "murabba"], cal: 49, pro: 0.1, per: "1 tbsp" },
  { keywords: ["nutella"], cal: 80, pro: 1, per: "1 tbsp" },
  { keywords: ["honey", "asal"], cal: 60, pro: 0, per: "1 tbsp" },
  { keywords: ["maple syrup", "maple"], cal: 52, pro: 0, per: "1 tbsp" },
  { keywords: ["sugar", "sokkar"], cal: 16, pro: 0, per: "1 tsp" },
  { keywords: ["brown sugar", "sokkar asmar"], cal: 15, pro: 0, per: "1 tsp" },
  { keywords: ["agave syrup"], cal: 60, pro: 0, per: "1 tbsp" },
  { keywords: ["stevia"], cal: 0, pro: 0, per: "1 tsp" },

  // ═══════════════════════════════════════════════════════════════════
  // BEVERAGES — Non-alcoholic
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["water", "maya"], cal: 0, pro: 0, per: "any amount" },
  { keywords: ["black coffee", "ahwa sada", "espresso"], cal: 2, pro: 0.3, per: "1 cup" },
  { keywords: ["coffee with milk", "ahwa bil laban"], cal: 50, pro: 2.5, per: "1 cup" },
  { keywords: ["latte", "cafe latte"], cal: 100, pro: 6, per: "1 cup" },
  { keywords: ["cappuccino"], cal: 80, pro: 4, per: "1 cup" },
  { keywords: ["black tea", "shai"], cal: 2, pro: 0, per: "1 cup" },
  { keywords: ["tea with milk", "shai bil laban"], cal: 40, pro: 2, per: "1 cup" },
  { keywords: ["green tea", "shai akhdar"], cal: 2, pro: 0, per: "1 cup" },
  { keywords: ["herbal tea", "shai a3shab"], cal: 5, pro: 0, per: "1 cup" },
  { keywords: ["chamomile", "babounaj"], cal: 5, pro: 0, per: "1 cup" },
  { keywords: ["orange juice", "3asir bortoan"], cal: 45, pro: 0.7, per: "100ml" },
  { keywords: ["apple juice", "3asir tofah"], cal: 46, pro: 0.1, per: "100ml" },
  { keywords: ["mango juice", "3asir manga"], cal: 60, pro: 0.4, per: "100ml" },
  { keywords: ["pomegranate juice", "3asir rommaan"], cal: 54, pro: 0.2, per: "100ml" },
  { keywords: ["carrot juice", "3asir gazar"], cal: 40, pro: 0.9, per: "100ml" },
  { keywords: ["tomato juice", "3asir tamatem"], cal: 17, pro: 0.8, per: "100ml" },
  { keywords: ["sugarcane juice", "3asir asab"], cal: 55, pro: 0.2, per: "100ml" },
  { keywords: ["lemon juice", "3asir limoun"], cal: 22, pro: 0.4, per: "100ml" },
  { keywords: ["lemonade", "limounada"], cal: 40, pro: 0.1, per: "100ml" },
  { keywords: ["smoothie", "smoothi"], cal: 80, pro: 2, per: "100ml" },
  { keywords: ["protein smoothie", "smoothi protein"], cal: 150, pro: 20, per: "300ml" },
  { keywords: ["coconut water", "maya goz el hind"], cal: 19, pro: 0.7, per: "100ml" },
  { keywords: ["soda", "cola", "pepsi", "kola"], cal: 42, pro: 0, per: "100ml" },
  { keywords: ["diet coke", "coke zero", "pepsi diet"], cal: 1, pro: 0, per: "100ml" },
  { keywords: ["sprite", "7up"], cal: 39, pro: 0, per: "100ml" },
  { keywords: ["energy drink", "energy drink redbull"], cal: 45, pro: 0, per: "100ml" },
  { keywords: ["sports drink", "gatorade", "powerade"], cal: 26, pro: 0, per: "100ml" },
  { keywords: ["milk shake", "milkshake"], cal: 112, pro: 3.8, per: "100ml" },
  { keywords: ["hot chocolate", "kakao"], cal: 70, pro: 3.5, per: "1 cup" },
  { keywords: ["iced coffee", "cold coffee"], cal: 60, pro: 2, per: "1 cup" },
  { keywords: ["frappuccino"], cal: 180, pro: 3, per: "1 cup" },
  { keywords: ["turkish coffee", "ahwa turki"], cal: 5, pro: 0.3, per: "1 cup" },

  // Alcoholic
  { keywords: ["beer", "bira"], cal: 43, pro: 0.4, per: "100ml" },
  { keywords: ["wine", "nebeet"], cal: 83, pro: 0.1, per: "100ml" },
  { keywords: ["whiskey", "wiski"], cal: 250, pro: 0, per: "100ml" },
  { keywords: ["vodka", "vodka"], cal: 231, pro: 0, per: "100ml" },

  // ═══════════════════════════════════════════════════════════════════
  // SNACKS, SWEETS & TREATS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["dark chocolate"], cal: 155, pro: 2, per: "30g" },
  { keywords: ["milk chocolate", "chocolate"], cal: 170, pro: 2.5, per: "30g" },
  { keywords: ["white chocolate", "chocolate abiad"], cal: 175, pro: 2, per: "30g" },
  { keywords: ["kitkat"], cal: 520, pro: 7, per: "100g" },
  { keywords: ["snickers"], cal: 489, pro: 8, per: "100g" },
  { keywords: ["twix"], cal: 495, pro: 5, per: "100g" },
  { keywords: ["bounty"], cal: 485, pro: 4, per: "100g" },
  { keywords: ["mars bar", "mars"], cal: 449, pro: 4, per: "100g" },
  { keywords: ["chips", "potato chips", "crispy"], cal: 530, pro: 6, per: "100g" },
  { keywords: ["doritos"], cal: 480, pro: 7, per: "100g" },
  { keywords: ["cheetos"], cal: 547, pro: 6, per: "100g" },
  { keywords: ["pretzels"], cal: 380, pro: 10, per: "100g" },
  { keywords: ["popcorn caramel"], cal: 430, pro: 4, per: "100g" },
  { keywords: ["popcorn salted"], cal: 375, pro: 11, per: "100g" },
  { keywords: ["cookie", "baskweet", "biscuit"], cal: 50, pro: 0.6, per: "1 cookie" },
  { keywords: ["oreo"], cal: 53, pro: 0.5, per: "1 cookie" },
  { keywords: ["digestive biscuit"], cal: 70, pro: 1, per: "1 biscuit" },
  { keywords: ["wafer"], cal: 50, pro: 0.7, per: "1 wafer" },
  { keywords: ["cake slice"], cal: 350, pro: 4, per: "1 slice" },
  { keywords: ["cheesecake", "cheezkeik"], cal: 321, pro: 5.5, per: "100g" },
  { keywords: ["brownie"], cal: 415, pro: 5, per: "100g" },
  { keywords: ["muffin"], cal: 375, pro: 5, per: "1 muffin" },
  { keywords: ["donut", "doughnut"], cal: 400, pro: 5, per: "1 donut" },
  { keywords: ["cinnamon roll"], cal: 310, pro: 5, per: "1 roll" },
  { keywords: ["danish pastry"], cal: 374, pro: 6, per: "100g" },
  { keywords: ["eclair", "ecler"], cal: 280, pro: 5, per: "100g" },
  { keywords: ["macaron"], cal: 400, pro: 4, per: "100g" },
  { keywords: ["halva", "halawa"], cal: 469, pro: 12, per: "100g" },
  { keywords: ["loukoum", "Turkish delight"], cal: 323, pro: 0, per: "100g" },
  { keywords: ["gummy bears", "halawiyat"],  cal: 330, pro: 6.5, per: "100g" },
  { keywords: ["lollipop", "halwa 3asaya"], cal: 60, pro: 0, per: "1 pop" },
  { keywords: ["chewing gum"], cal: 7, pro: 0, per: "1 piece" },

  // ═══════════════════════════════════════════════════════════════════
  // BREAKFAST FOODS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["ful sandwich", "ful el sebah"], cal: 300, pro: 12, per: "1 sandwich" },
  { keywords: ["eggs with bread", "beid we eish"], cal: 290, pro: 16, per: "1 serving" },
  { keywords: ["feta with bread", "jibna we eish"], cal: 270, pro: 10, per: "1 serving" },
  { keywords: ["halawa with bread", "halawa we eish"], cal: 350, pro: 8, per: "1 serving" },
  { keywords: ["honey with bread", "asal we eish"], cal: 260, pro: 5, per: "1 serving" },
  { keywords: ["labneh with bread", "labneh we eish"], cal: 290, pro: 9, per: "1 serving" },
  { keywords: ["fool medames breakfast plate"], cal: 350, pro: 15, per: "1 plate" },
  { keywords: ["shakshuka"], cal: 120, pro: 8, per: "100g" },
  { keywords: ["avocado toast"], cal: 220, pro: 6, per: "1 slice" },
  { keywords: ["french toast", "khobz maqli"], cal: 150, pro: 5, per: "1 slice" },
  { keywords: ["overnight oats"], cal: 180, pro: 8, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // SPICES & HERBS (negligible cal but tracked for completeness)
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["salt", "malh"], cal: 0, pro: 0, per: "any amount" },
  { keywords: ["black pepper", "filfil aswad"], cal: 6, pro: 0.2, per: "1 tsp" },
  { keywords: ["cumin", "kamoun"], cal: 8, pro: 0.4, per: "1 tsp" },
  { keywords: ["turmeric", "kurkum"], cal: 8, pro: 0.2, per: "1 tsp" },
  { keywords: ["cinnamon", "irfa"], cal: 6, pro: 0.1, per: "1 tsp" },
  { keywords: ["paprika", "filfil ahmar"], cal: 6, pro: 0.3, per: "1 tsp" },
  { keywords: ["oregano", "za3tar"], cal: 5, pro: 0.2, per: "1 tsp" },
  { keywords: ["thyme", "za'atar"], cal: 4, pro: 0.2, per: "1 tsp" },
  { keywords: ["chili powder", "shatta"], cal: 6, pro: 0.3, per: "1 tsp" },
  { keywords: ["ginger", "ganzabeel"], cal: 4, pro: 0.1, per: "1 tsp" },
  { keywords: ["cardamom", "hil"], cal: 6, pro: 0.2, per: "1 tsp" },
  { keywords: ["parsley", "ba'dunis"], cal: 36, pro: 3, per: "100g" },
  { keywords: ["coriander", "kuzbara"], cal: 23, pro: 2.1, per: "100g" },
  { keywords: ["mint", "na3na3"], cal: 44, pro: 3.3, per: "100g" },
  { keywords: ["dill", "shabat"], cal: 43, pro: 3.5, per: "100g" },
  { keywords: ["basil", "rihan"], cal: 23, pro: 3.2, per: "100g" },

  // ═══════════════════════════════════════════════════════════════════
  // PACKAGED / PROCESSED FOODS
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["instant noodles", "maggi", "indomie"], cal: 350, pro: 8, per: "1 packet" },
  { keywords: ["instant soup", "shorbah fasoria"], cal: 60, pro: 2, per: "1 packet" },
  { keywords: ["canned beans", "fasolya 3ilba"], cal: 130, pro: 8, per: "100g" },
  { keywords: ["canned tuna", "tuna 3ilba"], cal: 115, pro: 26, per: "100g" },
  { keywords: ["canned corn", "dora 3ilba"], cal: 83, pro: 3, per: "100g" },
  { keywords: ["canned tomatoes", "tamatem 3ilba"], cal: 24, pro: 1.1, per: "100g" },
  { keywords: ["baked beans"], cal: 94, pro: 5, per: "100g" },
  { keywords: ["spam", "corned beef can"], cal: 309, pro: 14, per: "100g" },
  { keywords: ["frozen pizza"], cal: 260, pro: 10, per: "100g" },
  { keywords: ["frozen fries"], cal: 152, pro: 2.4, per: "100g" },
  { keywords: ["cereal bar", "bar habboub"], cal: 120, pro: 2, per: "1 bar" },

  // ═══════════════════════════════════════════════════════════════════
  // RESTAURANT STAPLES
  // ═══════════════════════════════════════════════════════════════════
  { keywords: ["caesar salad with croutons"], cal: 130, pro: 5, per: "100g" },
  { keywords: ["club sandwich"], cal: 380, pro: 20, per: "1 sandwich" },
  { keywords: ["grilled cheese sandwich"], cal: 350, pro: 14, per: "1 sandwich" },
  { keywords: ["BLT sandwich"], cal: 380, pro: 16, per: "1 sandwich" },
  { keywords: ["egg sandwich", "beid sandwich"], cal: 260, pro: 12, per: "1 sandwich" },
  { keywords: ["tuna sandwich"], cal: 300, pro: 22, per: "1 sandwich" },
  { keywords: ["chicken wrap"], cal: 350, pro: 25, per: "1 wrap" },
  { keywords: ["falafel sandwich", "taameya sandwich"], cal: 310, pro: 9, per: "1 sandwich" },
  { keywords: ["gyros"], cal: 380, pro: 25, per: "1 wrap" },
  { keywords: ["pho"], cal: 65, pro: 5, per: "100ml" },
  { keywords: ["pad thai"], cal: 180, pro: 10, per: "100g" },
  { keywords: ["tom yum soup"], cal: 40, pro: 3, per: "100ml" },
  { keywords: ["minestrone soup"], cal: 59, pro: 2.5, per: "100ml" },
  { keywords: ["french onion soup"], cal: 65, pro: 3, per: "100ml" },
  { keywords: ["clam chowder"], cal: 98, pro: 4.5, per: "100ml" },
  { keywords: ["chicken noodle soup"], cal: 45, pro: 4, per: "100ml" },
  { keywords: ["mushroom soup"], cal: 70, pro: 3, per: "100ml" },
  { keywords: ["grilled fish", "samak mashwi"], cal: 130, pro: 22, per: "100g" },
  { keywords: ["grilled steak", "steak mashwi"], cal: 218, pro: 28, per: "100g" },
  { keywords: ["grilled salmon"], cal: 185, pro: 25, per: "100g" },
  { keywords: ["mixed grill", "mashawi"], cal: 250, pro: 26, per: "100g" },
  { keywords: ["bbq ribs", "dulu3 mashwiya"], cal: 290, pro: 21, per: "100g" },

];

export default LOCAL_DB;